<template>
  <div>
    <!-- HERO -->
    <section class="hero">
      <div class="hero__overlay"></div>
      <header class="header">
        <AppLogo asLink to="/" size="md" :showText="true" />
      </header>
    </section>

    <!-- 메인 카드 -->
    <main class="main">
      <div class="container">
        <!-- Step 1: 비밀번호 입력 -->
        <div v-if="step === 1" class="card reset-card">
          <div class="reset-inner">
            <div class="reset-head">
              <div class="reset-badge">비밀번호 재설정</div>
              <h1 class="reset-title">Reset Password</h1>
            </div>

            <form class="reset-form" @submit.prevent="handleSubmit">
              <UiInput
                v-model="form.password"
                type="password"
                placeholder="새 비밀번호"
                autocomplete="new-password"
              />

              <UiInput
                v-model="form.passwordConfirm"
                type="password"
                placeholder="새 비밀번호 확인"
                autocomplete="new-password"
              />

              <UiButton
                type="submit"
                :disabled="!canSubmit || loading"
                :loading="loading"
              >
                다음
              </UiButton>
            </form>
          </div>
        </div>

        <!-- Step 2: 성공 화면 -->
        <div v-else class="card success-card">
          <div class="success-inner">
            <!-- 체크 아이콘 -->
            <div class="success-icon">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="50" fill="#2A4624" />
                <circle cx="50" cy="50" r="36" fill="#78FF47" />
                <path
                  d="M35 50 L45 60 L65 40"
                  stroke="#000000"
                  stroke-width="5"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <h2 class="success-title">비밀번호 변경이<br />완료되었습니다!</h2>
            <p class="success-message">
              보안을 위해서 재로그인을 진행해 주세요.
            </p>

            <UiButton @click="goToSignIn" class="success-btn">
              로그인으로 돌아가기
            </UiButton>
          </div>
        </div>
      </div>
    </main>

    <!-- 푸터 -->
    <footer class="footer" style="margin-top: var(--space-64)">
      <div class="footer-bar">
        <span class="hint">Copyright ⓒ 2025 Cardio Factory</span>
      </div>
    </footer>
  </div>
</template>

<script>
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AppLogo from "@/components/AppLogo.vue";
import { api } from "@/lib/api";

export default {
  name: "ResetPasswordView",
  components: { UiButton, UiInput, AppLogo },
  data() {
    return {
      step: 1, // 1 = 비밀번호 입력, 2 = 성공 화면
      loading: false,
      token: "",
      form: {
        password: "",
        passwordConfirm: "",
      },
    };
  },
  computed: {
    canSubmit() {
      const { password, passwordConfirm } = this.form;
      return (
        password &&
        passwordConfirm &&
        password === passwordConfirm &&
        password.length >= 8
      );
    },
  },
  async mounted() {
    // URL에서 토큰 추출
    this.token = this.$route.query.token;

    if (!this.token) {
      alert("유효하지 않은 링크입니다.");
      this.$router.push("/signin");
      return;
    }

    // 토큰 검증
    await this.verifyToken();
  },
  methods: {
    async verifyToken() {
      try {
        const res = await api("/v1/auth/reset-password/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: this.token }),
        });

        if (!res.ok) {
          throw new Error("토큰 검증 실패");
        }
      } catch (e) {
        console.error(e);
        alert("유효하지 않거나 만료된 링크입니다.");
        this.$router.push("/signin");
      }
    },

    async handleSubmit() {
      if (!this.canSubmit || this.loading) return;

      if (this.form.password !== this.form.passwordConfirm) {
        alert("비밀번호가 일치하지 않습니다.");
        return;
      }

      this.loading = true;

      try {
        const res = await api("/v1/auth/reset-password/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: this.token,
            new_password: this.form.password,
          }),
        });

        if (!res.ok) throw new Error("비밀번호 재설정 실패");

        // 성공 화면으로 전환
        this.step = 2;
      } catch (e) {
        console.error(e);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      } finally {
        this.loading = false;
      }
    },

    goToSignIn() {
      this.$router.push("/signin");
    },
  },
};
</script>

<style scoped>
.hero {
  height: 220px;
  position: relative;
}

/* 입력 카드 */
.reset-card {
  max-width: 758px;
  margin: 0 auto;
  padding: 48px 56px;
  background: #151718;
  border-radius: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
}

.reset-inner {
  max-width: 360px;
  margin: 0 auto;
}

.reset-head {
  margin-bottom: 24px;
  text-align: left;
}

.reset-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}

.reset-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

.reset-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

.reset-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
}

.reset-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.reset-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

.reset-form .ui-btn {
  width: 100%;
}

/* 성공 카드 */
.success-card {
  width: 480px;
  height: 597px;
  margin: 0 auto;
  background: #151718;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-inner {
  width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.success-icon {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.success-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 120%;
  letter-spacing: -2px;
  color: #ffffff;
  text-align: center;
}

.success-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 160%;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.8);
}

.success-btn {
  width: 100%;
  height: 54px;
  margin-top: 16px;
}

/* 푸터 */
.footer-bar {
  width: 100%;
  margin: 0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  background: #151718;
}

.hint {
  color: #8e8e8e;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 640px) {
  .reset-card,
  .success-card {
    padding: 28px 20px;
    border-radius: 20px;
    width: 100%;
    height: auto;
  }

  .reset-title,
  .success-title {
    font-size: 32px;
  }
}
</style>
